import { ApiError, commonFetch } from '../apiHelper';
import { CreateBranchRegistrationWorkflowDto, DeleteBranchRegistrationWorkflowDto, ExcludeBranchesFromRegistrationWorkflowDto, IncludeBranchesToRegistrationWorkflowDto, CompleteOwnFlowDto, OnboardingDto, onboardingDtoSchema, UpdateBranchRegistrationWorkflowCredentialsDto } from './onboardingTypes';

export class OnboardingApi {
  static get = async (): Promise<OnboardingDto | null> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    if (backendResponse.status === 404) {
      return null;
    }
    throw new ApiError(backendResponse);
  };

  static shareFlow = async (collaboratorEmail?: string): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/share`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        collaboratorEmail,
      }),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static getBySharedFlow = async (flowId: string, secret: string): Promise<OnboardingDto | null> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}?secret=${secret}`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    if (backendResponse.status === 404) {
      return null;
    }
    throw new ApiError(backendResponse);
  };

  static createBranchRegistrationWorkflow = async (dto: CreateBranchRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/create-branch-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static createBranchRegistrationWorkflowBySharedFlow = async (flowId: string, dto: CreateBranchRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/create-branch-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();  
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };
  
  static updateBranchRegistrationWorkflowCredentials = async (dto: UpdateBranchRegistrationWorkflowCredentialsDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/update-branch-registration-workflow-credentials`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static updateBranchRegistrationWorkflowCredentialsBySharedFlow = async (flowId: string, dto: UpdateBranchRegistrationWorkflowCredentialsDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/update-branch-registration-workflow-credentials`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();  
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };
  
  static excludeBranchesFromRegistrationWorkflow = async (dto: ExcludeBranchesFromRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/exclude-branches-from-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static excludeBranchesFromRegistrationWorkflowBySharedFlow = async (flowId: string, dto: ExcludeBranchesFromRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/exclude-branches-from-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static includeBranchesToRegistrationWorkflow = async (dto: IncludeBranchesToRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/include-branches-to-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static includeBranchesToRegistrationWorkflowBySharedFlow = async (flowId: string, dto: IncludeBranchesToRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/include-branches-to-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static deleteBranchRegistrationWorkflow = async (dto: DeleteBranchRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/delete-branch-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  }

  static deleteBranchRegistrationWorkflowBySharedFlow = async (flowId: string, dto: DeleteBranchRegistrationWorkflowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/delete-branch-registration-workflow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  }

  static completeAdminFlow = async (): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/current/complete-admin-flow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };

  static completeOwnFlow = async (flowId: string, dto: CompleteOwnFlowDto): Promise<OnboardingDto> => {
    const requestURL = `${process.env.REACT_APP_BACKEND_URL}/onboardings/shared/${flowId}/complete-own-flow`;
    const backendResponse = await commonFetch(requestURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
    });
    if (backendResponse.ok) {
      const json = await backendResponse.json();
      return onboardingDtoSchema.validateSync(json, { stripUnknown: true, abortEarly: false });
    }
    throw new ApiError(backendResponse);
  };
}

export default OnboardingApi;
